import React, { useEffect, useState } from 'react';
import { Option, Question } from '../Models';
import { PlusCircleIcon, TrashIcon, } from '@heroicons/react/20/solid';
import ConfirmModal from './Shared/ConfirmModal';
import { deleteAssessmentOptions, uploadQuestionImage } from '../api/assessmentquestion.api';
import useApiToken from '../hooks/useApiToken';


const CheckboxInput = ({ selectedQType, getOptionsfromChild, selectedOptions, isQuestionsModalOpen, singleSelect }: { isQuestionsModalOpen: boolean, selectedQType: Question, getOptionsfromChild: (o: Option[]) => void, selectedOptions: Option[] | undefined, singleSelect?: boolean }) => {
    const token = useApiToken();

    const [options, setOptions] = useState<Option[]>([
        {
            id: 1, text: '', isCorrect: false, order: 0
        }
    ]);

    const [optionInputModes, setOptionInputModes] = useState<Record<number, 'text' | 'image'>>({});
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [uploadingOptionId, setUploadingOptionId] = useState<number | null>(null);

    useEffect(() => {
        if (selectedOptions) {
            const ordered = [...selectedOptions].sort(
                (a, b) => (a.order ?? 0) - (b.order ?? 0)
            );
            setOptions(ordered);

            // Set option input mode for each option based on whether it has imageUrl
            if (selectedQType.title === 'IMAGE' && ordered.length > 0) {
                const modes: Record<number, 'text' | 'image'> = {};
                ordered.forEach(opt => {
                    modes[opt.id] = opt.imageUrl ? 'image' : 'text';
                });
                setOptionInputModes(modes);
            }
        }
    }, [selectedOptions, selectedQType.title])

    const toggleOptionInputMode = (optionId: number) => {
        setOptionInputModes(prev => ({
            ...prev,
            [optionId]: prev[optionId] === 'text' ? 'image' : 'text'
        }));
    };

    const getOptionInputMode = (optionId: number): 'text' | 'image' => {
        return optionInputModes[optionId] || 'text';
    };

    const handleInputChange = (index: number, value: string): void => {
        const updatedOptions = [...options];
        updatedOptions[index].text = value;
        setOptions(updatedOptions);
        getOptionsfromChild(updatedOptions);
    };

    const handleCheckboxChange = (index: number): void => {
        let renderedOptions;
        let updatedOptions = [...options];
        const shouldSingleSelect =
            typeof singleSelect === "boolean"
                ? singleSelect
                : selectedQType.title === 'SINGLE' || selectedQType.title === 'IMAGE';
        if (shouldSingleSelect) {
            renderedOptions = options.map((e) => {
                return { id: e.id, text: e.text, isCorrect: false, order: e.order, imageUrl: e.imageUrl }
            })
            updatedOptions = [...renderedOptions]
        }
        updatedOptions[index].isCorrect = !updatedOptions[index].isCorrect;
        setOptions(updatedOptions)
        getOptionsfromChild(updatedOptions);

    };

    const handleAdd = (e: any): void => {
        e.preventDefault();
        let tempOptions = options;
        const newId = options.length + 1;
        tempOptions.push({
            id: newId, text: '', isCorrect: false, order: tempOptions.length
        })
        setOptions([...tempOptions]);

        // Set default mode for new option
        if (selectedQType.title === 'IMAGE') {
            setOptionInputModes(prev => ({
                ...prev,
                [newId]: 'text'
            }));
        }
    }

    const handleDelete = (index: number): void => {
        setDeleteIndex(index);
        setIsModalOpen(true);
    };

    const confirmDelete = () => {
        if (deleteIndex !== null) {
            const updatedOptions = [...options];
            const currentDeletedOption = updatedOptions[deleteIndex]
            updatedOptions.splice(deleteIndex, 1);
            const reindexedOptions = updatedOptions.map((option, idx) => ({
                ...option,
                order: idx,
            }));
            setOptions(reindexedOptions);
            setIsModalOpen(false);
            setDeleteIndex(null);
            getOptionsfromChild(reindexedOptions);

            if (currentDeletedOption.id.toLocaleString().length > 5 && token) {
                //MAKE A DELETE OPTION CALL WITH INDEX
                deleteAssessmentOptions(currentDeletedOption, token);
            }
        }
    };

    const handleOptionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, optionId: number) => {
        const file = e.target.files?.[0];
        if (!file || !token) return;

        setUploadingOptionId(optionId);
        try {
            const imageUrl = await uploadQuestionImage(file, token);
            const updatedOptions = options.map((opt) =>
                opt.id === optionId ? { ...opt, imageUrl } : opt
            );
            setOptions(updatedOptions);
            getOptionsfromChild(updatedOptions);
        } catch (error) {
            console.error('Failed to upload option image:', error);
        } finally {
            setUploadingOptionId(null);
        }
    };


    return (
        <div className="w-[100%]">
            <div className={`flex flex-col items-start py-14 ${isQuestionsModalOpen ? "" : "border-b border-gray-300 dark:border-gray-600"} lg:flex-row `}>
                <div className="w-64">
                    <h3 className={`block  font-medium text-gray-700  text-left py-4 dark:bg-transparent dark:text-gray-400 ${isQuestionsModalOpen ? "text-xs" : "text-sm"}`}>Options <span className='text-red-700'>*</span></h3>
                </div>
                <div className='w-full'>
                    <>
                        {options.map((option, index) => {
                            const currentMode = getOptionInputMode(option.id);
                            const isImageMode = selectedQType.title === 'IMAGE' && currentMode === 'image';

                            return (
                                <div key={option.id} className="flex items-start mb-4 gap-2">
                                    <div className="w-full">
                                        {/* Toggle for each option when type is IMAGE */}
                                        {selectedQType.title === 'IMAGE' && (
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs text-gray-600 dark:text-gray-400">Text</span>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={currentMode === 'image'}
                                                        onChange={() => toggleOptionInputMode(option.id)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                                                </label>
                                                <span className="text-xs text-gray-600 dark:text-gray-400">Image</span>
                                            </div>
                                        )}

                                        {isImageMode ? (
                                            <div className="w-full">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleOptionImageUpload(e, option.id)}
                                                    disabled={uploadingOptionId === option.id}
                                                    className="block w-full text-sm text-gray-500 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:text-gray-400 dark:file:bg-indigo-900 dark:file:text-indigo-200"
                                                />
                                                {uploadingOptionId === option.id && (
                                                    <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">Uploading...</p>
                                                )}
                                                {option.imageUrl && (
                                                    <div className="mt-2">
                                                        <img src={option.imageUrl} alt={`Option ${option.id}`} className="max-w-xs rounded border border-gray-300 dark:border-gray-600" />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updatedOptions = options.map((opt) =>
                                                                    opt.id === option.id ? { ...opt, imageUrl: undefined } : opt
                                                                );
                                                                setOptions(updatedOptions);
                                                                getOptionsfromChild(updatedOptions);
                                                            }}
                                                            className="mt-1 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                        >
                                                            Remove Image
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="w-full">
                                                <input
                                                    type="text"
                                                    value={option.text}
                                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                                    className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-sm"
                                                    placeholder={`Enter text for Option ${option.id}`}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 mt-8">
                                        <input
                                            type="checkbox"
                                            id={`checkbox${option.id}`}
                                            checked={option.isCorrect}
                                            onChange={() => handleCheckboxChange(index)}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                        />
                                        <label htmlFor={`checkbox${option.id}`} className="text-left hidden sm:w-32 sm:block dark:text-gray-400 text-sm whitespace-nowrap" >
                                            Is Correct
                                        </label>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleDelete(index)
                                            }}
                                            className=""
                                        >
                                            <TrashIcon className="h-4 text-red-500" />
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </>
                    <div className="flex">
                        <button onClick={handleAdd} className="">
                            <PlusCircleIcon className="h-8 text-blue-700" />
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                onConfirm={confirmDelete}
                type="danger"
                title="Delete Option"
                description="Are you sure you want to delete this option?"
            />
        </div>
    );
};

export default CheckboxInput;
